"use client";

import { MailOpen } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/lib/analytics/hooks";
import { isValidEmailFormat } from "@/lib/apply/email-validate";
import { buildMagicLinkErrorCallbackURL } from "@/lib/auth/callback-url";
import { signIn } from "@/lib/auth/client";
import { rememberAuthMethod } from "@/lib/auth/last-method";
import { authErrorGeneric } from "@/lib/copy/feedback";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
// Pure, dependency-free (no server-only imports — see its own doc comment) —
// safe to bundle client-side for the employer door's business-email gate.
import { checkBusinessEmail } from "@/lib/recruiter/business-email";
import { verifyTurnstile } from "@/server/actions/turnstile";
import { LastUsedPill } from "./last-used-pill";
import type { TurnstileErrorReason } from "./turnstile-widget";
import { TurnstileWidget } from "./turnstile-widget";

type MagicLinkFormProps = {
	locale: Locale;
	t?: Dictionary;
	callbackURL?: string;
	/**
	 * Cloudflare Turnstile site key — passed in from the page so it can be
	 * sourced from the runtime Cloudflare env when build-time inlining didn't
	 * pick it up.
	 */
	turnstileSiteKey?: string;
	/**
	 * Whether Google/LinkedIn one-click signin is available on this page. When
	 * true, the Turnstile-failure state points users to OAuth — it bypasses the
	 * bot check entirely and one-click completes, recovering the Chrome/desktop
	 * users that `challenge_error` was otherwise blocking.
	 */
	oauthAvailable?: boolean;
	/** Show the "Last used" badge — email was the visitor's previous method. */
	lastUsed?: boolean;
	/** Localized "Last used" badge label. */
	lastUsedLabel?: string;
	/**
	 * True when this form is rendered on `/login` (vs `/signup`, which reuses
	 * this same component). Gates the `login_started` capture below so a
	 * signup-page submit doesn't fire a same-shaped event under the wrong name.
	 */
	isLoginPage?: boolean;
	/** Whether the page's URL had an explicit `callbackURL` — the cross-zone-kickout signal. Instrumentation only. */
	hasCallbackParam?: boolean;
	/** Whether the page's URL had an `error` param (e.g. an expired magic-link redirect). Instrumentation only. */
	hadErrorParam?: boolean;
	/**
	 * Employer door's business-email-only gate (`checkBusinessEmail`,
	 * src/lib/recruiter/business-email.ts): rejects free webmail/disposable
	 * domains client-side, before the Turnstile round trip. A plain boolean
	 * (not a function prop) because this form crosses the Server→Client
	 * Component boundary — functions defined in the server page can't be
	 * passed as props here. Defaults to `false`, so omitting it preserves
	 * the exact existing seeker /login and /signup behavior.
	 */
	requireBusinessEmail?: boolean;
	/**
	 * Override for the derived `errorCallbackURL` (defaults to
	 * `buildMagicLinkErrorCallbackURL(locale, callbackURL)`, which points at
	 * the seeker `/login`). The employer door passes its own so an
	 * expired/invalid link redirects back to `/employers/login`, not the
	 * seeker page.
	 */
	errorCallbackURL?: string;
	/**
	 * Called once a link was dispatched (the `signup_started` moment). A host
	 * that renders its own confirmation (the post-apply toast card, where the
	 * full "Check your inbox" screen would not fit) swaps the form out here.
	 * Absent = unchanged behaviour.
	 */
	onSent?: () => void;
	/** `id` of the email input (default `"email"`), for hosts that may share a
	 * page with another MagicLinkForm and need the label to stay unique. */
	emailInputId?: string;
};

/**
 * `sessionStorage` key holding the email of the last magic link this tab
 * successfully dispatched. Read on a fresh `/login?error=...` load (an
 * expired/invalid-token redirect — see `LoginPage`'s `hadErrorParam`) to
 * power a one-tap resend without asking the user to retype their email and
 * re-clear Turnstile. `sessionStorage`, not a cookie: never sent to the
 * server, cleared when the tab closes, and only ever holds an email the
 * user themselves just typed into this exact form — the least-exposure
 * option that still survives the error-redirect round trip. Mirrors
 * `last-method.ts`'s "no server-visible PII" stance for the auth surface.
 */
const PENDING_MAGIC_LINK_EMAIL_KEY = "ld_pending_magic_link_email";

export function MagicLinkForm({
	locale,
	t,
	callbackURL,
	turnstileSiteKey,
	oauthAvailable,
	lastUsed,
	lastUsedLabel,
	isLoginPage,
	hasCallbackParam,
	hadErrorParam,
	requireBusinessEmail,
	errorCallbackURL: errorCallbackURLOverride,
	onSent,
	emailInputId = "email",
}: MagicLinkFormProps) {
	const effectiveCallbackURL = callbackURL ?? `/${locale}`;
	// Where Better Auth's magic-link verify endpoint sends the user on failure
	// (expired/invalid token). See `buildMagicLinkErrorCallbackURL` for why this
	// must be set explicitly instead of left to Better Auth's callbackURL default.
	const errorCallbackURL =
		errorCallbackURLOverride ??
		buildMagicLinkErrorCallbackURL(locale, effectiveCallbackURL);
	const { posthog } = useAnalytics();
	const [email, setEmail] = useState("");
	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");
	const [errorMessage, setErrorMessage] = useState("");
	const [resendCountdown, setResendCountdown] = useState(60);
	const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent">(
		"idle",
	);
	const [resendKey, setResendKey] = useState(0);
	// One-tap resend on an expired/invalid magic-link redirect (`hadErrorParam`)
	// — the email this tab last successfully dispatched to, read back from
	// `sessionStorage`. Null until the effect below finds a stored value, so
	// the affordance stays hidden when we genuinely have nothing to prefill
	// (a fresh tab, a different browser, sessionStorage cleared).
	const [pendingResendEmail, setPendingResendEmail] = useState<string | null>(
		null,
	);
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const [turnstileState, setTurnstileState] = useState<
		"loading" | "ready" | "error"
	>("loading");
	const [turnstileKey, setTurnstileKey] = useState(0);
	// A wrong device clock needs different advice than a blocked script, so the
	// error copy follows the reason the widget gave up with.
	const [turnstileClockSkew, setTurnstileClockSkew] = useState(false);
	const handleTurnstileToken = useCallback((token: string | null) => {
		setTurnstileToken(token);
		if (token) {
			setTurnstileState("ready");
		} else {
			// Token cleared (expiry or challenge error) — widget is auto-resetting.
			// Show the loading hint so the button's disabled state is explained.
			setTurnstileState("loading");
		}
	}, []);
	const handleTurnstileError = useCallback(
		(reason: TurnstileErrorReason) => {
			// No silent remount: this form is the first thing a signup user touches
			// and the widget already auto-retried internally (maxRetryAttempts
			// below). The old internal-ladder × silent-remount combination held
			// "Verifying you're human…" for 40–80s before the error state (and its
			// soft-fail submit) unlocked — users bailed long before (signup
			// start→complete 45.5%, 2026-07-14 audit). Surface the recovery UI
			// (retry button + OAuth steer + soft-fail submit) as soon as the
			// widget gives up.
			//
			// This is the failure mode the user actually sees. Bucket it
			// explicitly (not a bare passthrough) so a newly added
			// `TurnstileErrorReason` literal is force-reviewed here instead of
			// silently missing from the funnel's failure-mode breakdown.
			switch (reason) {
				case "missing_sitekey":
				case "load_timeout":
				case "render_error":
				case "challenge_error":
				case "script_load_error":
				case "clock_skew":
					posthog.capture("turnstile_error_shown", {
						signup_method: "magic_link",
						reason,
					});
					break;
				default: {
					const _exhaustive: never = reason;
					throw new Error(`unhandled turnstile error reason: ${_exhaustive}`);
				}
			}
			setTurnstileClockSkew(reason === "clock_skew");
			setTurnstileToken(null);
			setTurnstileState("error");
		},
		[posthog],
	);
	const handleTurnstileRetry = useCallback(() => {
		posthog.capture("turnstile_widget_retry_clicked", {
			signup_method: "magic_link",
		});
		setTurnstileToken(null);
		setTurnstileState("loading");
		setTurnstileKey((k) => k + 1);
	}, [posthog]);

	// Turnstile retry-ladder analytics (`turnstile_error_shown`,
	// `turnstile_submit_blocked`, `turnstile_softfail_submit_attempted`,
	// `turnstile_softfail_verified`) fire as plain, non-keepalive
	// `posthog.capture()` calls. If the user closes/navigates away from the tab
	// mid-retry — a real failure-abandonment path — those in-flight events can
	// be dropped by the browser before PostHog's periodic batch flush sends
	// them, which is exactly the failure we most need visibility into. Close
	// that gap explicitly: while the widget sits in its terminal "error" state
	// (the retry/soft-fail window), listen for `pagehide` (not the deprecated
	// `beforeunload`, so this stays bfcache-compatible) and force a dedicated
	// abandonment event out via `sendBeacon` — a transport built for exactly
	// this "fire it now, the page may be gone a moment later" case. Skips if
	// the ladder already resolved (status === "success") so it only fires for
	// genuine abandonment.
	useEffect(() => {
		if (turnstileState !== "error") return;
		const handlePageHide = () => {
			if (status === "success") return;
			posthog.capture(
				"turnstile_retry_abandoned",
				{ signup_method: "magic_link", status },
				{ transport: "sendBeacon", send_instantly: true },
			);
		};
		window.addEventListener("pagehide", handlePageHide);
		return () => window.removeEventListener("pagehide", handlePageHide);
	}, [turnstileState, status, posthog]);

	// One-tap resend impression: fires once, only when we actually have a
	// stored email to offer AND the page landed here via the expired/invalid
	// token redirect — never on a plain first visit to /login.
	useEffect(() => {
		if (!hadErrorParam || typeof window === "undefined") return;
		let stored: string | null = null;
		try {
			stored = window.sessionStorage.getItem(PENDING_MAGIC_LINK_EMAIL_KEY);
		} catch {
			// Storage disabled (private mode / blocked) — degrade to the normal form.
			return;
		}
		if (!stored) return;
		setPendingResendEmail(stored);
		setEmail(stored);
		posthog.capture("magic_link_resend_shown", { locale });
	}, [hadErrorParam, locale, posthog]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: resendKey is an intentional reset trigger — incrementing it re-runs this effect to start a fresh countdown after a resend without changing `status`
	useEffect(() => {
		if (status !== "success") return;
		setResendCountdown(60);
		const id = setInterval(() => {
			setResendCountdown((n) => {
				if (n <= 1) {
					clearInterval(id);
					return 0;
				}
				return n - 1;
			});
		}, 1000);
		return () => clearInterval(id);
	}, [status, resendKey]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		// Validate and send the SAME trimmed value — native `type="email"` accepts
		// undeliverable shapes like `foo@bar` (no TLD) and leaves surrounding
		// whitespace, either of which would fire a magic-link email into the void
		// (a wasted send + a dead end). Require a dotted domain before sending.
		const trimmedEmail = email.trim();
		if (!trimmedEmail) return;
		if (!isValidEmailFormat(trimmedEmail)) {
			setStatus("error");
			setErrorMessage(t?.invalidEmail || "Please enter a valid email address.");
			return;
		}
		if (requireBusinessEmail && !checkBusinessEmail(trimmedEmail).ok) {
			setStatus("error");
			setErrorMessage(
				t?.businessEmailRequired ||
					(locale === "id"
						? "Gunakan email perusahaan — email gratisan (Gmail, Yahoo, dll.) tidak diterima untuk akun employer."
						: "Use your company email — free webmail (Gmail, Yahoo, etc.) isn't accepted for an employer account."),
			);
			return;
		}
		// The widget itself has already exhausted its own retry ladder — let the
		// server decide via the flag-gated soft-fail path (see
		// src/server/actions/turnstile.ts) instead of blocking the user outright.
		const softFailAttempt = !turnstileToken && turnstileState === "error";
		// Defensive event, expected to almost never fire (telemetry-gap-remediation
		// 2026-07-21 audit): the submit button is disabled until a token exists OR
		// the widget has reached its terminal "error" state (softFailAttempt), so
		// this branch only catches a race (token expiring between render and
		// submit) — a near-zero count here is correct, not a gate bug. Leave as-is.
		if (!turnstileToken && !softFailAttempt) {
			posthog.capture("turnstile_submit_blocked", {
				signup_method: "magic_link",
				widget_state: turnstileState,
			});
			setStatus("error");
			setErrorMessage(
				t?.botCheckRequired ||
					"Please complete the bot check before continuing.",
			);
			return;
		}

		setStatus("loading");
		setErrorMessage("");
		// Normalize state to the trimmed value so the success screen + resend
		// (which read `email`) match exactly what was sent.
		setEmail(trimmedEmail);

		try {
			if (softFailAttempt) {
				posthog.capture("turnstile_softfail_submit_attempted", {
					signup_method: "magic_link",
				});
			}
			const verify = await verifyTurnstile(turnstileToken, "magic-link");
			if (!verify.ok) {
				posthog.capture("turnstile_verify_failed", {
					signup_method: "magic_link",
					error: verify.error,
				});
				setStatus("error");
				setErrorMessage(
					t?.botCheckFailed ||
						"Bot check failed. Please refresh the page and try again.",
				);
				return;
			}
			if (verify.softfail) {
				posthog.capture("turnstile_softfail_verified", {
					signup_method: "magic_link",
				});
			}

			const { error } = await signIn.magicLink({
				email: trimmedEmail,
				callbackURL: effectiveCallbackURL,
				errorCallbackURL,
			});

			if (error) {
				setStatus("error");
				setErrorMessage(
					error.message || t?.magicLinkError || "Failed to send magic link",
				);
			} else {
				posthog.capture("signup_started", {
					signup_method: "magic_link",
					email_domain: trimmedEmail.split("@")[1],
					callback_url: effectiveCallbackURL,
				});
				if (isLoginPage) {
					posthog.capture("login_started", {
						method: "magic_link",
						has_callback: Boolean(hasCallbackParam),
						had_error_param: Boolean(hadErrorParam),
					});
				}
				// Remember email for the "Last used" hint on the next visit.
				rememberAuthMethod("email");
				// Remember the dispatched address for the one-tap resend effect above
				// — best-effort, never blocks the send it's recording.
				try {
					window.sessionStorage?.setItem(
						PENDING_MAGIC_LINK_EMAIL_KEY,
						trimmedEmail,
					);
				} catch {
					// Storage disabled — the resend affordance just won't prefill next time.
				}
				setStatus("success");
				onSent?.();
			}
		} catch (_error) {
			setStatus("error");
			setErrorMessage(t?.unexpectedError || authErrorGeneric(locale));
		}
	};

	// Same `signIn.magicLink()` dispatch used by `handleSubmit` above — this is
	// the one send path for the whole form. Two callers: the "Check your
	// inbox" screen's countdown-gated resend button, and the one-tap resend
	// affordance below (rendered on an expired/invalid-token redirect, before
	// `status` ever reaches "success"). `setStatus("success")` is a no-op for
	// the first caller (already there) and is what lands the second caller on
	// the same "Check your inbox" screen instead of a bespoke confirmation UI.
	const handleResend = async () => {
		setResendStatus("sending");
		try {
			const { error } = await signIn.magicLink({
				email,
				callbackURL: effectiveCallbackURL,
				errorCallbackURL,
			});
			if (!error) {
				setResendStatus("sent");
				setResendKey((k) => k + 1);
				setStatus("success");
			} else {
				setResendStatus("idle");
			}
		} catch {
			setResendStatus("idle");
		}
	};

	// One-tap resend click — same dispatch as `handleResend`, plus the click
	// telemetry paired with the `magic_link_resend_shown` impression above.
	const handleOneTapResend = () => {
		posthog.capture("magic_link_resend_clicked", { locale });
		void handleResend();
	};

	if (status === "success") {
		return (
			<div
				className="flex flex-col items-center gap-5 text-center"
				data-testid="magic-link-sent"
			>
				<div className="bg-primary/10 rounded-full p-4">
					<MailOpen className="size-8 text-primary" />
				</div>
				<div>
					<h3 className="font-semibold text-lg mb-1">
						{t?.checkInbox || "Check your inbox"}
					</h3>
					<p className="text-sm text-muted-foreground">
						{t?.magicLinkSent?.replace("{email}", email) ||
							`We've sent a magic link to ${email}. Click it to sign in.`}
					</p>
				</div>
				{/* No /70: fails WCAG AA 4.5:1 in both themes. */}
				<p className="text-xs text-muted-foreground">
					{t?.checkSpam || "Not in your inbox? Check your spam folder."}
				</p>
				<button
					type="button"
					onClick={handleResend}
					disabled={resendCountdown > 0 || resendStatus === "sending"}
					className="text-sm text-primary underline-offset-4 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
				>
					{resendStatus === "sent"
						? t?.magicLinkResent || "Magic link resent"
						: resendCountdown > 0
							? (t?.resendIn || "Resend in {seconds}s").replace(
									"{seconds}",
									String(resendCountdown),
								)
							: t?.resendMagicLink || "Resend magic link"}
				</button>
				<button
					type="button"
					onClick={() => {
						setStatus("idle");
						setEmail("");
						setResendStatus("idle");
					}}
					className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
				>
					{t?.useDifferentEmail || "Use a different email"}
				</button>
			</div>
		);
	}

	return (
		// suppressHydrationWarning: password-manager extensions (e.g. LastPass)
		// inject icon elements into the form before React hydrates, causing a
		// server/client HTML mismatch that is not actionable in application code.
		<form
			onSubmit={handleSubmit}
			className="flex flex-col gap-4"
			suppressHydrationWarning
		>
			{/* One-tap resend — expired/invalid-token redirect (`hadErrorParam`)
			    landed here with a dispatched email still in `sessionStorage`. Skips
			    retyping the email + re-clearing Turnstile; a tap re-sends straight
			    to "Check your inbox" via `handleResend` (the same dispatch used
			    below), no full form submission needed. */}
			{hadErrorParam && pendingResendEmail && (
				<div
					data-testid="magic-link-one-tap-resend"
					className="flex flex-col gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3"
				>
					<p className="text-sm text-foreground">
						{(
							t?.magicLinkResendPrompt || "Resend the sign-in link to {email}?"
						).replace("{email}", pendingResendEmail)}
					</p>
					<Button
						type="button"
						onClick={handleOneTapResend}
						disabled={resendStatus === "sending"}
						size="sm"
						shape="lg"
						className="min-h-11"
					>
						{resendStatus === "sending"
							? t?.sending || "Sending..."
							: t?.resendMagicLink || "Resend magic link"}
					</Button>
				</div>
			)}

			<div className="flex flex-col gap-2">
				<label htmlFor={emailInputId} className="text-sm font-medium">
					{t?.emailAddress || "Email address"}
				</label>
				<input
					id={emailInputId}
					type="email"
					required
					placeholder={t?.emailPlaceholder || "you@example.com"}
					className="w-full px-4 py-3 bg-muted/30 border border-border/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					disabled={status === "loading"}
				/>
			</div>

			<TurnstileWidget
				key={turnstileKey}
				siteKey={turnstileSiteKey}
				action="magic-link"
				locale={locale}
				// `action` is a fixed literal shared by /login and /signup — `surface`
				// is what lets turnstile_widget_error be sliced per page.
				surface={
					requireBusinessEmail
						? "employer_signup"
						: isLoginPage
							? "login"
							: "signup"
				}
				onToken={handleTurnstileToken}
				onError={handleTurnstileError}
				// One internal retry only (~5-10s to the terminal state), not the
				// full 3-step ladder — see handleTurnstileError above.
				maxRetryAttempts={1}
			/>

			{turnstileState === "loading" && (
				<p
					id="turnstile-status-hint"
					className="text-xs text-muted-foreground"
					data-testid="turnstile-loading"
				>
					{t?.botCheckLoading || "Verifying you're human…"}
				</p>
			)}
			{turnstileState === "error" && (
				<div
					id="turnstile-status-hint"
					className="flex flex-col gap-2"
					data-testid="turnstile-error"
				>
					<p
						className="text-sm text-destructive"
						data-testid={
							turnstileClockSkew ? "turnstile-clock-skew" : undefined
						}
					>
						{turnstileClockSkew
							? t?.botCheckClockSkew ||
								"Your device's date or time is wrong, so the bot check can't pass. Turn on automatic date & time, then tap Try again."
							: t?.botCheckUnavailable ||
								"Couldn't load the bot check. Disable any ad blocker or extensions blocking challenges.cloudflare.com."}
					</p>
					<button
						type="button"
						onClick={handleTurnstileRetry}
						className="self-start inline-flex min-h-11 items-center px-2 text-sm text-primary underline-offset-4 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						data-testid="turnstile-retry"
					>
						{t?.botCheckRetry || "Try again"}
					</button>
					{oauthAvailable ? (
						<div
							data-testid="turnstile-oauth-hint"
							className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5"
						>
							<p className="text-sm font-medium text-foreground">
								{t?.botCheckUseOauthTitle || "Skip the bot check entirely"}
							</p>
							<p className="mt-0.5 text-xs text-muted-foreground">
								{t?.botCheckUseOauth ||
									"No bot check needed — sign up with Google or LinkedIn above."}
							</p>
						</div>
					) : null}
				</div>
			)}

			{/*
			 * data-testid: the terminal FAILURE branch of this form, the mirror of
			 * `magic-link-sent` below. Without it a failed submit is indistinguishable
			 * from a slow one in e2e — signup-real-form.spec.ts could only report
			 * "magic-link-sent element(s) not found", which names neither the branch
			 * that actually rendered nor why. Making the branch explicit in the UI is
			 * this repo's rule for exactly this case (tests/e2e/CLAUDE.md → "Need a
			 * stable handle? Add one to the component").
			 */}
			{status === "error" && (
				<p className="text-sm text-destructive" data-testid="magic-link-error">
					{errorMessage}
				</p>
			)}

			<Button
				type="submit"
				disabled={
					status === "loading" ||
					!email ||
					(!turnstileToken && turnstileState !== "error")
				}
				aria-describedby={
					!turnstileToken && turnstileState !== "ready"
						? "turnstile-status-hint"
						: undefined
				}
				size="lg"
				shape="pill"
				fullWidth
				className="relative tracking-tight"
			>
				{status === "loading"
					? t?.sending || "Sending..."
					: t?.sendMagicLink || "Send Magic Link"}
				{lastUsed && lastUsedLabel ? (
					<LastUsedPill label={lastUsedLabel} tone="onPrimary" />
				) : null}
			</Button>
		</form>
	);
}
